import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SupplierDashboard from './SupplierDashboard';
import ProcurementDashboard from './ProcurementDashboard';
import LegalDashboard from './LegalDashboard';
import DepartmentLeadDashboard from './DepartmentLeadDashboard';
import Overview from './Overview';
import { checkSupplierProfileComplete } from '../../utils/profileCheck';

/**
 * Routes each role to the page it should land on. Nothing is rendered here but
 * the profile gate — every branch below owns its own data.
 */
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    const checkProfile = async () => {
      if (user?.role === 'supplier') {
        const profileComplete = await checkSupplierProfileComplete(user);
        if (!profileComplete) {
          navigate('/profile');
          return;
        }
      }
      setProfileChecked(true);
    };

    if (user) {
      checkProfile();
    }
  }, [user, navigate]);

  // The /dashboard/stats call that used to live here fed the four inline cards
  // this component no longer renders. Every branch below fetches what it needs,
  // so keeping it would have been a request whose result nothing reads.
  if (!profileChecked) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Supplier Dashboard
  if (user.role === 'supplier') {
    return <SupplierDashboard />;
  }

  // Procurement Dashboard
  if (user.role === 'procurement') {
    return <ProcurementDashboard />;
  }

  // Legal Dashboard
  if (user.role === 'legal') {
    return <LegalDashboard />;
  }

  // Super Admin Dashboard
  //
  // Was UserManagement — a super admin landed on a user table, which answers
  // no question about how onboarding is going. The user table has not moved;
  // it is reachable at /users, which the sidebar now points at.
  if (user.role === 'super_admin') {
    return <Overview />;
  }

  // Department Lead Dashboard (Management role)
  if (user.role === 'management') {
    return <DepartmentLeadDashboard />;
  }

  // Unreachable in practice: User.role is an enum of exactly the five roles
  // handled above. Kept as a guard so a record with a missing or unrecognised
  // role renders something rather than undefined. It used to be four static
  // cards with no pipeline, departments or ageing — the Overview is a better
  // thing to fall back to, but nothing is expected to land here.
  return <Overview />;
};

export default Dashboard;
